import type { Application, RegistryState, UnitClass } from '../../data/types';
import type { Readiness } from '../kyc/readiness';

export type PaymentStatus = 'NOT_PAID' | 'PART_PAID' | 'PAID';

export interface ApplicationPayment {
  payableNow: number;
  payableLater: number;
  received: number;
  outstanding: number;
  status: PaymentStatus;
}

const round2 = (value: number) => Math.round(value * 100) / 100;

export function paidFraction(unitClass?: UnitClass): number {
  return unitClass?.paidOnApplicationPct ? unitClass.paidOnApplicationPct / 100 : 1;
}

export function payableNowFor(amount: number, unitClass?: UnitClass): number {
  return round2(amount * paidFraction(unitClass));
}

export function applicationPayment(
  application: Application,
  unitClass?: UnitClass,
): ApplicationPayment {
  const payableNow = application.payableNow ?? payableNowFor(application.amount, unitClass);
  const received = application.receipts
    ? round2(application.receipts.reduce((sum, receipt) => sum + receipt.amount, 0))
    : application.fundsReceivedAt
      ? payableNow
      : 0;
  const outstanding = Math.max(0, round2(payableNow - received));
  return {
    payableNow,
    payableLater: round2(application.amount - payableNow),
    received,
    outstanding,
    status: outstanding <= 0.005 ? 'PAID' : received > 0 ? 'PART_PAID' : 'NOT_PAID',
  };
}

export function crnFor(seed: string): string {
  let hash = 7;
  for (const char of seed) hash = (hash * 31 + char.charCodeAt(0)) % 1_000_000_000;
  return String(hash).padStart(9, '0');
}

const NOT_YET_SUBMITTED = new Set(['INVITED', 'IN_PROGRESS']);
const FINISHED = new Set(['ISSUED', 'REJECTED', 'WITHDRAWN']);

export function closeExclusion(
  application: Application,
  payment: ApplicationPayment,
  readiness?: Readiness,
): string | undefined {
  if (FINISHED.has(application.status)) return undefined;
  if (application.issueRunId) return 'Already in a unit issue';
  if (NOT_YET_SUBMITTED.has(application.status)) return 'Application not submitted';
  if (application.status === 'KYC_REVIEW') return 'KYC review open';
  const blocking = readiness?.issues.find((issue) => issue.blocking);
  if (blocking) return blocking.text;
  if (payment.status === 'NOT_PAID') return 'Not paid';
  if (payment.status === 'PART_PAID') return 'Part paid';
  return undefined;
}

export function classApplications(state: RegistryState, classId: string) {
  return state.applications.filter(
    (application) => application.classId === classId && !FINISHED.has(application.status),
  );
}

export function nextCertificateSequence(state: RegistryState, classId: string): number {
  const setup = state.fundSetups.find((item) => item.classId === classId);
  const start = Number(setup?.values['cert.startSeq']) || 1;
  const used = state.issueRuns
    .filter((run) => run.classId === classId)
    .flatMap((run) => run.lines)
    .map((line) => Number(line.certificateNo?.split('-').pop()))
    .filter((value) => Number.isFinite(value));
  return used.length ? Math.max(...used) + 1 : start;
}

export function certificatePrefix(state: RegistryState, classId: string): string {
  const unitClass = state.classes.find((item) => item.id === classId);
  const fund = state.funds.find((item) => item.id === unitClass?.fundId);
  const setup = state.fundSetups.find((item) => item.classId === classId);
  const company = setup?.values['cert.companyCode'] ?? fund?.code ?? 'FUND';
  const security = setup?.values['cert.securityCode'] ?? unitClass?.code ?? 'ORD';
  return `${String(company)}-${String(security)}`;
}
